///
/// EarthBrowser
///
/// Created by Matt Giger on 12/30/2023
/// Copyright (c) 2023 Geodata Labs
///

import { round, trig } from "@stovokor/math/math"
import { matrix } from "@stovokor/math/matrix"
import { vec3 } from "@stovokor/math/vector"
import { MeasurementType, shouldUseUSCustomary } from "@/earthbrowser/utils/UnitSystem"

const scale			= 0.001

/**
 * Represents the World Geodetic System (WGS) constants.
 * These constants are used for geographic calculations and transformations.
 */
export class wgs {
	/** Earth's equatorial radius in kilometers */
	public static a: number				= 6378137.0 * scale

	/** Earth's polar radius in kilometers */
	public static b: number				= 6356752.3142 * scale

	/** Earth's ellipsoidal eccentricity */
	public static e: number				= 8.1819190842622e-2 * scale

	/** Square of the equatorial radius */
	public static a2: number			= wgs.a * wgs.a

	/** Square of the polar radius */
	public static b2: number			= wgs.b * wgs.b

	/** Square of the ellipsoidal eccentricity */
	public static e2: number			= wgs.e * wgs.e

	/** Ratio of squared polar radius to squared equatorial radius */
	public static b2a2: number			= wgs.b2 / wgs.a2
}

/**
 * Represents a geographic coordinate with longitude, latitude, and altitude.
 */
export class geographic {
	public readonly longitude: number
	public readonly latitude: number
	public readonly altitude: number

	/**
	 * Creates a new geographic coordinate from a cartesian vec3 or individual values.
	 * @param longitude - The longitude in radians. Defaults to 0. If a vec3 is provided, the longitude is calculated from a cartesian vector.
	 * @param latitude - The latitude in radians. Defaults to 0.
	 * @param altitude - The altitude in kilometers. Defaults to 0.
	 */
	constructor(longitude: vec3 | number = 0, latitude: number | undefined = 0, altitude: number | undefined = 0) {
		if(longitude instanceof vec3) {
			const len = longitude.length
			this.longitude = Math.atan2(longitude.x, longitude.z)
			this.latitude = trig.half_pi - Math.acos(longitude.y / len)
			this.altitude = len - wgs.a
		} else {
			this.longitude = longitude
			this.latitude = latitude
			this.altitude = altitude
		}
	}

	public get normal(): vec3 {
		const coslat = Math.cos(this.latitude)
		return new vec3(
			Math.sin(this.longitude) * coslat,
			Math.sin(this.latitude),
			Math.cos(this.longitude) * coslat
		)
	}

	/**
	 * Converts the geographic coordinate to Cartesian coordinates.
	 * @returns A vec3 representing the Cartesian coordinates.
	 */
	public get cartesian(): vec3 {
		const elev = this.altitude + wgs.a
		const coslat = Math.cos(this.latitude) * elev
		return new vec3(
			Math.sin(this.longitude) * coslat,
			Math.sin(this.latitude) * elev,
			Math.cos(this.longitude) * coslat
		)
	}

	/**
	 * Calculates the approximate visible distance on Earth's surface.
	 * @returns The visible distance in kilometers.
	 */
	public get visibleDistance(): number {
		// Approximate visible distance on Earth's surface
		// d ≈ √(2 * R * h + h²)
		const a = this.altitude
		return Math.sqrt(2*wgs.a*a + a*a)
	}

	/**
	 * Generates a transformation matrix that rotates and moves geometry to this geographic location
	 * with the positive y-axis aligned to the earth's radius at that point.
	 * @returns A matrix representing the transformation.
	 */
	public yAxisToRadiallyAligned(): matrix {
		const translation = matrix.translate(this.cartesian)
		const yRot = matrix.axisRotation(vec3.unitY, this.longitude)// + trig.pi)
		const xRot = matrix.axisRotation(vec3.unitX, this.latitude)// + trig.half_pi)
		return xRot.multiply(yRot).multiply(translation)
	}

	/**
	 * Converts decimal degrees to degrees and minutes format
	 */
	private toDM(decimalDegrees: number, isLatitude: boolean, includeSeconds: boolean): string {
		const abs = Math.abs(decimalDegrees)
		const degrees = Math.floor(abs)
		const minutesDecimal = (abs - degrees) * 60
		const minutes = Math.floor(minutesDecimal)

		// Determine direction
		let direction: string
		if(isLatitude) {
			direction = decimalDegrees >= 0 ? 'N' : 'S'
		} else {
			direction = decimalDegrees >= 0 ? 'E' : 'W'
		}

		if(includeSeconds) {
			const seconds = round((minutesDecimal - minutes) * 60, 1)
			return `${degrees}°${minutes}'${seconds}"${direction}`
		} else {
			const minutesWithDecimal = round(minutesDecimal, 1)
			return `${degrees}°${minutesWithDecimal}'${direction}`
		}
	}

	/**
	 * Returns a string representation of the geographic coordinate.
	 * @param includeSeconds - If true, display seconds in DMS format. If false, display only degrees and minutes. Default: false.
	 * @returns A string in DMS format with altitude.
	 * - With seconds: "41°52'41.2"N, 87°37'47.3"W 1234 m"
	 * - Without seconds: "41°52.7'N, 87°37.8'W 1234 m"
	 * Altitude unit is determined by browser locale: meters (most countries) or feet (US, Canada, UK territories).
	 */
	public toString(includeSeconds: boolean = false): string {
		const latDeg = this.latitude * trig.deg
		const lonDeg = this.longitude * trig.deg

		const latDM = this.toDM(latDeg, true, includeSeconds)
		const lonDM = this.toDM(lonDeg, false, includeSeconds)
		const coordString = `${latDM}, ${lonDM}`

		if(shouldUseUSCustomary(MeasurementType.ALTITUDE)) {
			// Convert from km to meters, then to feet
			const altM = this.altitude * 1000
			const altFt = Math.round(altM * 3.28084)
			return `${coordString} ${altFt} ft`
		} else {
			// Convert from km to meters
			const altM = Math.round(this.altitude * 1000)
			return `${coordString} ${altM} m`
		}
	}
}

/**
 * Represents a geographic coordinate with latitude, longitude, and altitude in degrees and meters.
 */
export function latlonalt(latitude: number, longitude: number, altitude: number): geographic {
	return new geographic(longitude * trig.rad, latitude * trig.rad, altitude)
}
